import { KB_LIFT_TRANSITION } from '../../components/common/keyboardOverlayStyles';
import { findBodyScroller } from './modalHeightFreeze';
import { flipSlide } from './modalAnticipationSlide';

/**
 * Content glide (one keyboard motion). When the inline guard / field reveal
 * scrolls a body scroller INSIDE a modal, the content jumps in one frame. We
 * never touch `scrollTop` (iOS must keep sampling the revealed layout); the
 * scroller's element children are FLIPped instead (`flipSlide`: counter-
 * transform at rAF #1, animate to 0 at rAF #2, lenient clear afterwards).
 *
 * `record(field)` runs from the hook's WINDOW-capture focusin (before the
 * guard's document-capture handler); `attach()` adds the DIALOG-level capture
 * focusin listener (runs after the guard) which reads the new scrollTop.
 */

function movable(child, rect, pad) {
  const pos = window.getComputedStyle(child).position;
  if (pos === 'sticky' || pos === 'fixed') return false;
  const r = child.getBoundingClientRect();
  return r.bottom >= rect.top - pad && r.top <= rect.bottom + pad;
}

export function createContentFlip(dialogEl) {
  let before = null; // { scroller, top, startAfterStall }
  let flips = [];

  const cancel = () => { flips.forEach((c) => c()); flips = []; };

  const start = (scroller, delta, startAfterStall = false) => {
    cancel();
    const rect = scroller.getBoundingClientRect();
    const pad = Math.abs(delta);
    flips = Array.from(scroller.children)
      .filter((c) => movable(c, rect, pad))
      .map((c) => {
        const own = c.style.transition;
        const list = own && own !== 'none' ? `${KB_LIFT_TRANSITION}, ${own}` : KB_LIFT_TRANSITION;
        return flipSlide(c, delta, { transition: list, startAfterStall });
      });
  };

  const onDialogFocusIn = () => {
    const b = before;
    before = null;
    if (!b || !b.scroller.isConnected) return;
    const delta = b.scroller.scrollTop - b.top; // content moved UP by delta
    if (delta) start(b.scroller, delta, b.startAfterStall);
  };

  return {
    /** `startAfterStall` (KAN-930, rest cards): see `flipSlide`. */
    record(field, { startAfterStall = false } = {}) {
      const scroller = findBodyScroller(dialogEl, field);
      before = scroller ? { scroller, top: scroller.scrollTop, startAfterStall } : null;
    },
    attach() { dialogEl.addEventListener('focusin', onDialogFocusIn, true); },
    /** A re-tap: stop running flips at once. */
    cancel,
    dispose() {
      dialogEl.removeEventListener('focusin', onDialogFocusIn, true);
      before = null;
      cancel();
    },
  };
}
